/**
 * Property dashboard data — integration layer between backend contracts and UI.
 * Fetches: plants-latest (or RPC fallback), poll status, capacity, forecast-runway, overnight forecast, energy guidance.
 * Applies: poll-status, energy-state, readiness.
 * Consistency with fleet: docs/contracts/FLEET_PROPERTY_CONSISTENCY.md — same sources and rules
 * (poll-status 15/30 min, energy_systems, overnight_forecasts, curtailment, resilience) so property and fleet show the same information.
 */

import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { getReachabilityStatus, type PollStatus } from '@/lib/poll-status';
import { getReadinessState, type ReadinessState } from '@/lib/readiness';
import { normalizeBatteryPowerForDisplay, getBatteryStateForDisplay, getSystemEnergyStateDisplay } from '@/lib/energy-state';
import { getFunctionsErrorBody, formatFunctionsError, isNoTelemetryError, isAbortError, is404Error } from '@/lib/functions-error';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/lib/vocabulary';
import { useOvernightForecast } from '@/hooks/useOvernightForecast';
import { useEnergyGuidance, type GuidanceForecastOverride } from '@/hooks/useEnergyGuidance';
import { extractRunwaySunriseKwh, readBackendAbsoluteKwh } from '@/lib/dashboard-forecast';
import type { TelemetryData, ForecastData, SystemRiskState } from '@/types/dashboard';

export interface PropertyDashboardData {
  telemetry: TelemetryData | null;
  telemetryLoading: boolean;
  telemetryFetching: boolean;
  telemetryError: string | null;
  refetchTelemetry: () => void;
  pollStatus: PollStatus;
  lastPollAt: string | null;
  lastPollStatus: string | null;
  pollLabel: string;
  /** When offline, normalized reason for last poll failure (credential error / timeout / API unavailable). */
  lastPollError: string | null;
  /** Capacity used for display (effective when available, else configured). */
  capacityForUI: number;
  /** Effective capacity from backend (battery_capacity_snapshots); null if not computed. */
  effectiveCapacityKwh: number | null;
  /** Configured capacity (property battery_kwh_total or default). */
  configuredCapacityKwh: number;
  readinessState: ReadinessState;
  selectedProperty: { id: string; name?: string; battery_kwh_total?: number; battery_reserve_pct?: number; readiness_state?: string; timezone?: string; grid_mode?: 'off_grid' | 'grid_primary' | null } | undefined;
  selectedPlant: { id: string; display_name: string; property_id: string; tier?: 'monitor' | 'alert' | 'predict' } | undefined;
  overnightForecast: ReturnType<typeof useOvernightForecast>['data'];
  overnightForecastLoading: boolean;
  overnightForecastError: Error | null;
  energyGuidance: ReturnType<typeof useEnergyGuidance>['data'];
  energyGuidanceLoading: boolean;
  energyGuidanceError: Error | null;
  /** Forecast-runway chart data (right column EnergyOutlook). */
  forecastRunwayData: ForecastData | null;
  forecastRunwayLoading: boolean;
  forecastRunwayError: string | null;
  refetchForecastRunway: () => void;
  /** Phase 2: grid_primary resilience risk active (from property_resilience_status). */
  resilienceRisk: boolean;
  resilienceSeverity: 'warning' | 'critical' | null;
  /** "Xh YYm" or "Passed" from sunrise time in forecast. */
  timeToSunrise: string;
  systemRiskState: SystemRiskState;
  systemEnergyState: 'charging' | 'discharging' | 'idle';
  netPower: number;
  noTelemetry: boolean;
}

function mapRpcRowToTelemetryData(row: Record<string, unknown>, plantId: string): TelemetryData {
  const pvW = Number(row.plant_pv_power_w ?? 0);
  const loadW = Number(row.plant_load_power_w ?? 0);
  const battW = Number(row.plant_battery_power_w ?? 0);
  const displayBattKw = normalizeBatteryPowerForDisplay(battW);
  const batteryState = getSystemEnergyStateDisplay(getBatteryStateForDisplay(displayBattKw, pvW / 1000, loadW / 1000));
  return {
    soc_pct: Number(row.plant_soc_pct ?? 0),
    pvPower_w: pvW,
    loadPower_w: loadW,
    battPower_w: battW,
    gridPower_w: row.plant_grid_power_w != null ? Number(row.plant_grid_power_w) : undefined,
    genPower_w: row.plant_gen_power_w != null ? Number(row.plant_gen_power_w) : undefined,
    last_updated: typeof row.bucket_ts === 'string' ? row.bucket_ts : '',
    batteryState,
  };
}

const FLEET_CACHE_REFRESH_THROTTLE_MS = 2 * 60 * 1000;

export function usePropertyDashboardData(propertyId: string | undefined, selectedPlantId: string | null): PropertyDashboardData {
  const { properties, plants, isHomeowner } = useApp();
  const plantsLatestUnavailableRef = useRef(false);
  const lastFleetCacheRefreshRef = useRef<{ propertyId: string; at: number } | null>(null);

  const {
    data: telemetry,
    isLoading: telemetryLoading,
    isFetching: telemetryFetching,
    error: telemetryQueryError,
    refetch: refetchTelemetry,
  } = useQuery({
    queryKey: ['plants-latest', selectedPlantId],
    queryFn: async (): Promise<TelemetryData | null> => {
      if (!selectedPlantId) return null;
      const tryRpcFallback = async (): Promise<TelemetryData | null> => {
        const { data: rows, error: rpcError } = await supabase.rpc('get_latest_plant_telemetry', {
          p_plant_id: selectedPlantId,
        });
        if (rpcError || !Array.isArray(rows) || rows.length === 0) return null;
        const row = (rows[0] ?? {}) as Record<string, unknown>;
        if (row.plant_soc_pct === undefined && row.plant_soc_pct === null) return null;
        return mapRpcRowToTelemetryData(row, selectedPlantId);
      };
      if (plantsLatestUnavailableRef.current) return tryRpcFallback();
      try {
        const { data, error } = await supabase.functions.invoke('plants-latest', {
          body: { plantId: selectedPlantId },
        });
        if (error) {
          if (error.message?.includes('No telemetry')) return null;
          if (is404Error(error)) {
            plantsLatestUnavailableRef.current = true;
            return tryRpcFallback();
          }
          throw error;
        }
        if (!data || data.soc_pct === undefined) return null;
        return data as TelemetryData;
      } catch (err) {
        if (is404Error(err)) {
          plantsLatestUnavailableRef.current = true;
          return tryRpcFallback();
        }
        if (err instanceof Error && err.message?.includes('No telemetry')) return null;
        throw err;
      }
    },
    enabled: !!selectedPlantId,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    placeholderData: (previousData) => previousData,
    retry: (failureCount, error) => (is404Error(error) || isAbortError(error) ? false : failureCount < 2),
  });

  const { data: reachabilityData } = useQuery({
    queryKey: ['property-reachability', propertyId],
    queryFn: async (): Promise<{
      lastProviderOkAt: string | null;
      lastProviderAuthFailedAt: string | null;
      lastPollAt: string | null;
      lastPollStatus: string | null;
      lastTelemetryAt: string | null;
    }> => {
      if (!propertyId)
        return {
          lastProviderOkAt: null,
          lastProviderAuthFailedAt: null,
          lastPollAt: null,
          lastPollStatus: null,
          lastTelemetryAt: null,
        };
      const { data: rows, error } = await supabase
        .from('energy_systems')
        .select('last_provider_ok_at, last_provider_auth_failed_at, last_poll_at, last_poll_status, last_non_empty_telemetry_at')
        .eq('property_id', propertyId)
        .eq('is_active', true);
      if (error) throw error;
      type Row = { last_provider_ok_at?: string | null; last_provider_auth_failed_at?: string | null; last_poll_at?: string | null; last_poll_status?: string | null; last_non_empty_telemetry_at?: string | null };
      const typedRows = (rows ?? []) as Row[];
      let bestOk: string | null = null;
      let worstAuth: string | null = null;
      let bestPollAt: string | null = null;
      let bestPollStatus: string | null = null;
      let lastTelemetryAt: string | null = null;
      for (const r of typedRows) {
        const okAt = r.last_provider_ok_at ?? null;
        if (okAt && (!bestOk || okAt > bestOk)) bestOk = okAt;
        const authAt = r.last_provider_auth_failed_at ?? null;
        if (authAt && (!worstAuth || authAt > worstAuth)) worstAuth = authAt;
        const at = r.last_poll_at ?? null;
        if (at && (!bestPollAt || at > bestPollAt)) {
          bestPollAt = at;
          bestPollStatus = r.last_poll_status ?? null;
        }
        const nonEmptyAt = r.last_non_empty_telemetry_at ?? null;
        if (nonEmptyAt && (!lastTelemetryAt || nonEmptyAt > lastTelemetryAt)) lastTelemetryAt = nonEmptyAt;
      }
      return {
        lastProviderOkAt: bestOk,
        lastProviderAuthFailedAt: worstAuth,
        lastPollAt: bestPollAt,
        lastPollStatus: bestPollStatus,
        lastTelemetryAt,
      };
    },
    enabled: !!propertyId,
    staleTime: 30 * 1000,
  });

  const { data: capacitySnapshot } = useQuery({
    queryKey: ['battery-capacity-snapshot', propertyId],
    queryFn: async (): Promise<{ effective_capacity_kwh: number | null } | null> => {
      if (!propertyId) return null;
      const { data, error: err } = await supabase
        .from('battery_capacity_snapshots')
        .select('effective_capacity_kwh')
        .eq('property_id', propertyId)
        .order('local_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (err) throw err;
      return data;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: overnightForecast, isLoading: overnightForecastLoading, error: overnightForecastError } = useOvernightForecast(propertyId);

  const { data: resilienceStatus } = useQuery({
    queryKey: ['property-resilience-status', propertyId],
    queryFn: async (): Promise<{ risk_active: boolean; severity: 'warning' | 'critical' | null } | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from('property_resilience_status')
        .select('risk_active, severity, last_evaluated_at, updated_at')
        .eq('property_id', propertyId)
        .maybeSingle();
      if (error) throw error;
      if (!data?.risk_active) return data;
      const evaluatedAt = data.last_evaluated_at ?? data.updated_at;
      const STALE_MS = 6 * 60 * 60 * 1000;
      if (!evaluatedAt || Date.now() - new Date(evaluatedAt).getTime() > STALE_MS) {
        return { ...data, risk_active: false, severity: null };
      }
      return data;
    },
    enabled: !!propertyId,
    staleTime: 60 * 1000,
  });

  const lastValidForecastRef = useRef<ForecastData | null>(null);
  const {
    data: forecastRunwayData,
    isLoading: forecastRunwayLoading,
    isError: forecastRunwayIsError,
    error: forecastRunwayQueryError,
    refetch: refetchForecastRunway,
  } = useQuery({
    queryKey: ['forecast-runway', propertyId, selectedPlantId],
    queryFn: async (): Promise<ForecastData | null> => {
      if (!propertyId || !selectedPlantId) return null;
      const body = { propertyId, plantId: selectedPlantId, horizonHours: 24 };
      const { data, error } = await supabase.functions.invoke('forecast-runway', { method: 'POST', body });
      if (error) {
        const backend = await getFunctionsErrorBody(error);
        if (backend && isNoTelemetryError(backend)) return null;
        if (backend) throw new Error(formatFunctionsError(backend));
        throw error;
      }
      if (data?.chartData) {
        lastValidForecastRef.current = data as ForecastData;
        return data as ForecastData;
      }
      return lastValidForecastRef.current;
    },
    enabled: !!propertyId && !!selectedPlantId,
    staleTime: 30 * 1000,
    refetchInterval: (query) => (query.state.status === 'error' ? 120_000 : 30_000),
    placeholderData: (previousData) => previousData ?? lastValidForecastRef.current,
    retry: (failureCount, error) => {
      if (isAbortError(error)) return false;
      return failureCount < 2;
    },
  });

  // Warm fleet cache so fleet dashboard can read from cache (incremental per-property).
  useEffect(() => {
    if (!propertyId) return;
    const hasData = telemetry !== undefined || reachabilityData !== undefined;
    if (!hasData) return;
    const now = Date.now();
    const last = lastFleetCacheRefreshRef.current;
    if (last?.propertyId === propertyId && now - last.at < FLEET_CACHE_REFRESH_THROTTLE_MS) return;
    lastFleetCacheRefreshRef.current = { propertyId, at: now };
    void supabase.rpc('refresh_fleet_cache_for_property', { p_property_id: propertyId });
  }, [propertyId, telemetry, reachabilityData]);

  const selectedPlant = plants.find((p) => p.id === selectedPlantId);
  const selectedProperty = properties.find((p) => p.id === propertyId);
  const readinessState = getReadinessState(selectedProperty);
  const lastPollError = selectedPlant?.last_poll_error ?? null;
  const effectiveCapacityKwh = capacitySnapshot?.effective_capacity_kwh ?? null;
  const configuredCapacityKwh = selectedProperty?.battery_kwh_total ?? 30;
  const capacityForUI = effectiveCapacityKwh ?? configuredCapacityKwh;

  const lastProviderOkAt = reachabilityData?.lastProviderOkAt ?? null;
  const lastProviderAuthFailedAt = reachabilityData?.lastProviderAuthFailedAt ?? null;
  const lastTelemetryAt = reachabilityData?.lastTelemetryAt ?? null;
  const lastPollAt = reachabilityData?.lastPollAt ?? null;
  const lastPollStatus = reachabilityData?.lastPollStatus ?? null;
  const pollStatus = getReachabilityStatus(lastTelemetryAt, lastProviderOkAt, lastProviderAuthFailedAt);
  const pollLabel =
    lastProviderOkAt == null
      ? t('neverPolled', isHomeowner)
      : lastProviderAuthFailedAt && lastProviderAuthFailedAt > lastProviderOkAt
        ? `${t('pollFailed', isHomeowner)} ${formatDistanceToNow(new Date(lastProviderAuthFailedAt), { addSuffix: true })}`
        : `${t('lastPoll', isHomeowner)} ${formatDistanceToNow(new Date(lastProviderOkAt), { addSuffix: true })}`;

  const { systemEnergyState, netPower } = (() => {
    if (!telemetry) return { systemEnergyState: 'idle' as const, netPower: 0 };
    const pvW = telemetry.pvPower_w || 0;
    const loadW = telemetry.loadPower_w || 0;
    const genW = telemetry.genPower_w || 0;
    const gridW = telemetry.gridPower_w || 0;
    const netPower = pvW + genW + gridW - loadW;
    const displayBattKw = normalizeBatteryPowerForDisplay(telemetry.battPower_w || 0);
    const state = getBatteryStateForDisplay(displayBattKw, pvW / 1000, loadW / 1000);
    return { systemEnergyState: state, netPower };
  })();

  const runwayData = forecastRunwayData ?? lastValidForecastRef.current;
  const timeToSunrise = (() => {
    const sunrise = runwayData?.sunriseTime;
    if (!sunrise) return '--:--';
    const now = new Date();
    const sunriseDate = new Date(sunrise);
    const diffMins = differenceInMinutes(sunriseDate, now);
    if (diffMins <= 0) return 'Passed';
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  })();

  // Build forecast override for energy-guidance backend (aligns decision with dashboard display).
  // Prefer the backend absolute-kWh contract (Wave 1 / WT-145/176): the guidance
  // CONTROL decision must not depend on a frontend SoC × display-capacity recompute.
  const guidanceForecastOverride: GuidanceForecastOverride | null = (() => {
    if (!runwayData || forecastRunwayLoading) return null;
    const reservePct = (selectedProperty?.battery_reserve_pct ?? 20) / 100;
    const backendAbs = readBackendAbsoluteKwh(runwayData);
    if (backendAbs) {
      return {
        expectedAtSunriseKwh: backendAbs.expected,
        worstAtSunriseKwh: backendAbs.worst,
        // Backend's own reserve energy; fall back to reservePct × observed capacity.
        reserveThresholdKwh: backendAbs.reserveKwh ?? reservePct * capacityForUI,
        forecastSource: 'forecast-runway' as const,
      };
    }
    const sunrise = extractRunwaySunriseKwh(runwayData, capacityForUI);
    if (!sunrise) return null;
    return {
      expectedAtSunriseKwh: sunrise.expected,
      worstAtSunriseKwh: sunrise.worst,
      reserveThresholdKwh: reservePct * capacityForUI,
      forecastSource: 'forecast-runway' as const,
    };
  })();
  const { data: energyGuidance, isLoading: energyGuidanceLoading, error: energyGuidanceError } = useEnergyGuidance(propertyId, guidanceForecastOverride);

  // Suppress false breach warnings when battery is nearly full — forecast depletion
  // projections are irrelevant at high SoC (e.g. 100% off-grid in daytime).
  const socPct = telemetry?.soc_pct ?? 0;
  const systemRiskState: SystemRiskState =
    socPct >= 95
      ? 'ok'
      : runwayData?.depletionTimeExpected != null && String(runwayData.depletionTimeExpected).trim() !== ''
        ? 'action_required'
        : runwayData?.depletionTimeWorst != null && String(runwayData.depletionTimeWorst).trim() !== ''
          ? 'watch'
          : 'ok';

  const loading = telemetryLoading && !telemetry;
  const noTelemetry = !loading && telemetry === null && !!selectedPlantId;
  const forecastRunwayError =
    forecastRunwayIsError && forecastRunwayQueryError
      ? (forecastRunwayQueryError as Error).message
      : null;

  return {
    telemetry: telemetry ?? null,
    telemetryLoading,
    telemetryFetching,
    telemetryError: telemetryQueryError ? (telemetryQueryError as Error).message : null,
    refetchTelemetry,
    pollStatus,
    lastPollAt,
    lastPollStatus,
    pollLabel,
    lastPollError,
    capacityForUI,
    effectiveCapacityKwh,
    configuredCapacityKwh,
    readinessState,
    selectedProperty,
    selectedPlant,
    overnightForecast: overnightForecast ?? null,
    overnightForecastLoading,
    overnightForecastError: overnightForecastError ?? null,
    energyGuidance: energyGuidance ?? null,
    energyGuidanceLoading,
    energyGuidanceError: energyGuidanceError ?? null,
    forecastRunwayData: runwayData,
    forecastRunwayLoading: forecastRunwayLoading && !runwayData,
    forecastRunwayError,
    refetchForecastRunway,
    resilienceRisk: !!resilienceStatus?.risk_active,
    resilienceSeverity: (resilienceStatus?.severity === 'warning' || resilienceStatus?.severity === 'critical') ? resilienceStatus.severity : null,
    timeToSunrise,
    systemRiskState,
    systemEnergyState,
    netPower,
    noTelemetry,
  };
}
